import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

/**
 * Stock Counts — FR-INV-04.05 / 04.06 / 04.07
 * Cycle & full physical counts. Blind mode hides system qty from counters.
 */
export function StockCountsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showStart, setShowStart] = useState(false);

  const listQuery = useQuery({
    queryKey: ['inv', 'counts'],
    queryFn: () => inventoryApi.listCounts({ limit: 200 }),
  });

  const kpis = useMemo(() => {
    const rows = (listQuery.data ?? []) as any[];
    return {
      total: rows.length,
      inProgress: rows.filter((r) => r.status === 'in_progress').length,
      completed: rows.filter((r) => r.status === 'completed').length,
      blindOpen: rows.filter((r) => r.status === 'in_progress' && r.blind).length,
    };
  }, [listQuery.data]);

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Stock counts"
        subtitle="Cycle counts and full physical inventories. Variances automatically generate adjustments."
        actions={
          <button className="btn-primary" onClick={() => setShowStart(true)}>
            <PlusIcon /> Start count
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total counts" value={kpis.total} />
        <KpiTile label="In progress" value={kpis.inProgress}
          accent={kpis.inProgress > 0 ? 'amber' : 'default'} />
        <KpiTile label="Completed" value={kpis.completed} accent="sage" />
        <KpiTile label="Blind active" value={kpis.blindOpen} />
      </div>

      {/* Two-pane */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? (
            <LoadingState rows={5} />
          ) : listQuery.error ? (
            <ErrorState error={listQuery.error} />
          ) : !listQuery.data?.length ? (
            <EmptyState
              message="No counts yet"
              hint="Start a cycle or full count to reconcile system-vs-physical inventory."
              action={
                <button className="btn-primary" onClick={() => setShowStart(true)}>
                  <PlusIcon /> Start count
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Warehouse</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {(listQuery.data ?? []).map((c: any) => {
                    const pct = c.total_lines > 0 ? (c.counted_lines / c.total_lines) * 100 : 0;
                    return (
                      <tr
                        key={c.id}
                        onClick={() => setSelected(c.id)}
                        className={clsx('cursor-pointer', selected === c.id && '!bg-canvas-subtle')}
                      >
                        <td className="font-mono text-xs font-semibold text-ink-900">
                          {c.count_number}
                        </td>
                        <td className="text-sm text-ink-700 font-mono">{c.warehouse_code}</td>
                        <td>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 capitalize">
                              {c.count_type}
                            </span>
                            {c.blind && (
                              <span className="badge bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200 text-2xs">
                                Blind
                              </span>
                            )}
                          </span>
                        </td>
                        <td><CountStatusBadge status={c.status} /></td>
                        <td>
                          <div className="flex items-center gap-2 w-40">
                            <div className="flex-1 h-1.5 bg-canvas-subtle rounded-pill overflow-hidden">
                              <div
                                className={clsx(
                                  'h-full rounded-pill transition-all duration-500',
                                  pct >= 100 ? 'bg-sage-500' : 'bg-ink-400',
                                )}
                                style={{ width: `${Math.max(pct, 3)}%` }}
                              />
                            </div>
                            <span className="text-xs text-ink-500 font-mono tabular-nums min-w-[3.5rem] text-right">
                              {c.counted_lines}/{c.total_lines}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select a count"
              hint="Click a row on the left to record counts or review variances."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 2h6a2 2 0 012 2v2h2a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h2V4a2 2 0 012-2z"/>
                  <rect x="9" y="2" width="6" height="4" rx="1"/>
                </svg>
              }
            />
          ) : (
            <CountDetail
              countId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['inv', 'counts'] })}
            />
          )}
        </div>
      </div>

      {showStart && (
        <StartCountModal
          onClose={() => setShowStart(false)}
          onStarted={(id) => {
            qc.invalidateQueries({ queryKey: ['inv', 'counts'] });
            setShowStart(false);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function CountStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'completed'   ? 'sage' :
    status === 'in_progress' ? 'warning' :
                               'neutral';
  return <StatusBadge status={status.replace('_', ' ')} tone={tone as any} />;
}

// ============================================================
// CountDetail
// ============================================================
function CountDetail({ countId, onChanged }: { countId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const [showComplete, setShowComplete] = useState(false);
  const detailQuery = useQuery({
    queryKey: ['inv', 'count', countId],
    queryFn: () => inventoryApi.getCount(countId),
  });
  const [barcode, setBarcode] = useState('');
  const [qty, setQty] = useState('');

  const recordMut = useMutation({
    mutationFn: ({ productId, countedQty }: { productId: string; countedQty: number }) =>
      inventoryApi.recordCountLine(countId, { productId, countedQty }),
    onSuccess: (data) => {
      toast.success(`Variance: ${data.variance > 0 ? '+' : ''}${data.variance}`);
      qc.invalidateQueries({ queryKey: ['inv', 'count', countId] });
      setBarcode('');
      setQty('');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const completeMut = useMutation({
    mutationFn: () => inventoryApi.completeCount(countId),
    onSuccess: (data) => {
      if (data.adjustmentNumber) {
        toast.success(`Completed. Adjustment ${data.adjustmentNumber} created (${data.adjustmentStatus}) for ${data.varianceLineCount} variances.`, { duration: 6000 });
      } else {
        toast.success('Completed. No variances.');
      }
      qc.invalidateQueries({ queryKey: ['inv', 'count', countId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // #22: this hook must run on every render (before the early returns below), otherwise the
  // hook count changes between the loading and loaded renders -> React error #310 (crash).
  const varianceStats = useMemo(() => {
    let positive = 0, negative = 0, exact = 0, uncounted = 0;
    for (const l of (detailQuery.data?.lines ?? [])) {
      if (l.counted_qty === null) { uncounted++; continue; }
      const v = l.variance !== null ? parseFloat(l.variance) : null;
      if (v === null) continue;
      if (v > 0) positive++;
      else if (v < 0) negative++;
      else exact++;
    }
    return { positive, negative, exact, uncounted };
  }, [detailQuery.data]);

  if (detailQuery.isLoading) return <LoadingState rows={4} />;
  if (detailQuery.error) return <ErrorState error={detailQuery.error} />;
  const c = detailQuery.data;

  const recordByBarcode = async () => {
    const q = barcode.trim();
    const n = parseFloat(qty);
    if (!q || !(n >= 0)) {
      toast.error('Enter SKU/barcode and a non-negative quantity');
      return;
    }
    try {
      const results = await inventoryApi.listProducts({ q, pageSize: 1 });
      if (!results?.data?.[0]) {
        toast.error(`No product matches "${q}"`);
        return;
      }
      recordMut.mutate({ productId: results.data[0].id, countedQty: n });
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{c.count_number}</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">
            {c.warehouse_code} — {c.warehouse_name}
          </div>
          <div className="inline-flex items-center gap-1.5 mt-1">
            <span className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 capitalize">
              {c.count_type}
            </span>
            {c.blind && (
              <span className="badge bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200 text-2xs">
                Blind
              </span>
            )}
          </div>
        </div>
        <CountStatusBadge status={c.status} />
      </div>

      {/* Timeline */}
      <div className="bg-canvas-subtle rounded-xl p-3 mb-4 space-y-1.5 text-xs text-ink-600">
        {c.started_at && (
          <div>
            <span className="text-ink-400">Started:</span>{' '}
            {new Date(c.started_at).toLocaleString()} · {c.started_by_name}
          </div>
        )}
        {c.completed_at && (
          <div className="text-sage-700">
            <span className="text-sage-600">Completed:</span>{' '}
            {new Date(c.completed_at).toLocaleString()} · {c.completed_by_name}
          </div>
        )}
        {c.adjustment_number && (
          <div className="pt-1">
            <span className="text-ink-400">Generated adjustment:</span>{' '}
            <span className="font-mono font-semibold text-ink-900">{c.adjustment_number}</span>
          </div>
        )}
      </div>

      {/* Variance breakdown summary */}
      {(varianceStats.positive + varianceStats.negative + varianceStats.exact) > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          <VarianceStatTile label="Exact" value={varianceStats.exact} tone="sage" />
          <VarianceStatTile label="Short" value={varianceStats.negative} tone="danger" />
          <VarianceStatTile label="Over" value={varianceStats.positive} tone="warning" />
          <VarianceStatTile label="Uncounted" value={varianceStats.uncounted} tone="neutral" />
        </div>
      )}

      {/* Record entry form */}
      {c.status === 'in_progress' && (
        <div className="bg-canvas-subtle rounded-xl p-3 mb-4 border border-ink-100">
          <div className="eyebrow text-ink-500 mb-2">Record count</div>
          <div className="flex gap-2">
            <input
              className="input flex-1 text-sm font-mono"
              placeholder="SKU or barcode"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (document.getElementById(`qty-${countId}`) as HTMLInputElement)?.focus();
              }}
            />
            <input
              id={`qty-${countId}`}
              className="input w-24 text-sm text-right tabular-nums"
              type="number" min="0" step="1"
              placeholder="qty"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') recordByBarcode(); }}
            />
            <button
              className="btn-primary"
              disabled={recordMut.isPending}
              onClick={recordByBarcode}
            >
              Record
            </button>
          </div>
        </div>
      )}

      {/* Lines */}
      <div className="border border-ink-100 rounded-xl overflow-hidden max-h-96 overflow-y-auto">
        <table className="table text-sm">
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right">System</th>
              <th className="text-right">Counted</th>
              <th className="text-right">Variance</th>
            </tr>
          </thead>
          <tbody>
            {c.lines.map((l: any) => {
              const variance = l.variance !== null ? parseFloat(l.variance) : null;
              return (
                <tr key={l.id}>
                  <td>
                    <div className="text-xs font-medium text-ink-900">{l.product_name}</div>
                    <div className="font-mono text-2xs text-ink-500">{l.sku}</div>
                  </td>
                  <td className="text-right font-mono text-xs tabular-nums text-ink-700">
                    {c.blind && c.status === 'in_progress'
                      ? <span className="text-ink-300">—</span>
                      : (l.system_qty !== null ? parseFloat(l.system_qty).toFixed(0) : '—')}
                  </td>
                  <td className="text-right font-mono tabular-nums">
                    {l.counted_qty !== null
                      ? parseFloat(l.counted_qty).toFixed(0)
                      : <span className="text-ink-400 text-xs italic">not counted</span>}
                  </td>
                  <td className={clsx(
                    'text-right text-xs font-mono font-semibold tabular-nums',
                    variance === null ? 'text-ink-300' :
                    variance === 0    ? 'text-sage-700' :
                    variance < 0      ? 'text-danger' :
                                        'text-warning'
                  )}>
                    {variance !== null ? (variance > 0 ? `+${variance}` : variance) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {c.status === 'in_progress' && (
        <button
          className="btn-primary w-full mt-4"
          disabled={completeMut.isPending}
          onClick={() => setShowComplete(true)}
        >
          {completeMut.isPending ? 'Completing…' : 'Complete count'}
        </button>
      )}

      {showComplete && (
        <Modal
          onClose={() => setShowComplete(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow mb-1">Inventory · Stock count</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Complete this count?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              Any lines with variances will automatically generate a stock adjustment.
              The count will be locked and cannot be edited after completion.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setShowComplete(false)}>Cancel</button>
            <button
              className="btn-primary"
              disabled={completeMut.isPending}
              onClick={() => { completeMut.mutate(); setShowComplete(false); }}
            >
              {completeMut.isPending ? 'Completing…' : 'Complete count'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function VarianceStatTile({
  label, value, tone,
}: { label: string; value: number; tone: 'sage' | 'danger' | 'warning' | 'neutral' }) {
  const color = {
    sage:    'text-sage-700',
    danger:  'text-danger',
    warning: 'text-warning',
    neutral: 'text-ink-700',
  }[tone];
  return (
    <div className="bg-canvas-subtle rounded-md p-2 text-center">
      <div className={clsx('font-display font-bold text-xl tabular-nums', color)}>
        {value}
      </div>
      <div className="eyebrow text-2xs text-ink-500 mt-0.5">{label}</div>
    </div>
  );
}

// ============================================================
// StartCountModal
// ============================================================
function StartCountModal({ onClose, onStarted }: { onClose: () => void; onStarted: (id: string) => void }) {
  const [warehouseId, setWarehouseId] = useState('');
  const [countType, setCountType] = useState<'full' | 'cycle'>('full');
  const [blind, setBlind] = useState(false);
  const [notes, setNotes] = useState('');

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });

  const mut = useMutation({
    mutationFn: () => inventoryApi.startCount({
      warehouseId, countType, blind, notes: notes || undefined,
    }),
    onSuccess: (data) => {
      toast.success(`${data.countNumber} started — ${data.lineCount} products queued`);
      onStarted(data.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Inventory</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Start stock count</h2>
        <p className="text-sm text-ink-500 mt-1">
          A count snapshots current system quantities. Variances auto-generate an adjustment on completion.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Warehouse</label>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Select warehouse…</option>
            {whQuery.data?.map((w: any) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Count type</label>
          <select className="input" value={countType} onChange={(e) => setCountType(e.target.value as any)}>
            <option value="full">Full — every product in warehouse</option>
            <option value="cycle">Cycle — partial / by schedule</option>
          </select>
        </div>
        <label className="flex items-start gap-3 text-sm text-ink-700 bg-canvas-subtle rounded-xl px-3 py-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={blind}
            onChange={(e) => setBlind(e.target.checked)}
            className="mt-0.5 accent-sage-500"
          />
          <span>
            <span className="font-semibold">Blind count</span>
            <span className="text-ink-500 block text-xs mt-0.5">
              Hide system quantities from counters — forces honest recount rather than "matching" the expected value.
            </span>
          </span>
        </label>
        <div>
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Q1 cycle count, month-end full, etc." />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!warehouseId || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Starting…' : 'Start count'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function PlusIcon() { return <svg {...iconProps}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
